'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/transformers'

const AttentionSentence = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.AttentionSentence), { ssr: false })
const SoftLookup = dynamic(() => import('../_components/widgets/TransformerWidgets').then(m => m.SoftLookup), { ssr: false })

export default function Attention() {
  return (
    <Lesson>
      <Step gate="q-it">
        <H>What does “it” mean?</H>
        <P>
          Read this: <em>“The animal didn’t cross the street because it was too tired.”</em> What does “it” refer to? The animal.
          Now change one word: <em>“…because it was too wide.”</em> Now “it” is the street. To understand a word, you have to look
          at other words — and <strong>which</strong> other words matters depends on the whole sentence.
        </P>
        <P>
          Our neural language model from Module 8 had no way to do this: it glued a fixed window of letters together and treated
          every position the same. <strong>Attention</strong> gives every token the ability to look back at all the others and
          decide, for itself, which ones are relevant.
        </P>
        <Figure n="9.1" caption="An illustration of what a trained attention head might do. Tap ‘it’ and switch the last word.">
          <AttentionSentence />
        </Figure>
        <Quiz
          id="q-it"
          question="What does attention let each token do?"
          options={[
            { label: 'Build its new representation from a weighted mix of earlier tokens, choosing the weights based on content.', correct: true },
            { label: 'Look only at the token immediately before it.', why: 'That’s the bigram model. Attention can reach any earlier position.' },
            { label: 'Change the words of the sentence.', why: 'It changes the vectors representing the words, not the text itself.' },
          ]}
        />
      </Step>

      <Step>
        <H>A soft dictionary</H>
        <P>
          A normal dictionary lookup takes a <Hl c="accent">query</Hl>, finds the one <strong>key</strong> that matches exactly,
          and returns its <strong>value</strong>. Attention is a <em>soft</em> version: compare the query with <em>every</em>{' '}
          key (with a dot product — Module 3!), turn the match scores into weights with softmax (Module 5!), and return a
          weighted blend of all the values.
        </P>
        <MB>{'\\text{output} = \\sum_i \\text{softmax}(\\accent{\\mathbf{q}} \\cdot \\mathbf{k}_i)\\; \\mathbf{v}_i'}</MB>
        <Figure n="9.2" caption="Drag the query. Keys pointing the same way score higher, get more weight, and contribute more of their value to the output. ‘Sharpness’ turns a soft blend into a near-exact lookup.">
          <SoftLookup />
        </Figure>
        <Callout kind="plain">
          Every ingredient is something you already know: dot products to measure similarity, softmax to turn scores into
          weights, a weighted sum to blend. Attention is just those three, combined.
        </Callout>
      </Step>

      <Step gate="attend">
        <H>Write attention</H>
        <Exercise
          id="attend"
          title="A soft lookup"
          {...EX.attend}
          hints={[
            <>
              Scores: <C>{'keys.map(k => vec.dot(query, k))'}</C>, then <C>vec.softmax(scores)</C>.
            </>,
            <>
              Start <C>out</C> at zeros and add <C>vec.scale(values[i], w[i])</C> for each i.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-learned">
        <H>Where do queries, keys and values come from?</H>
        <P>
          In a transformer, every token produces all three, from its own embedding, using three learned matrices:{' '}
          <M>{'\\mathbf{q} = \\mathbf{x}W_Q'}</M>, <M>{'\\mathbf{k} = \\mathbf{x}W_K'}</M>, <M>{'\\mathbf{v} = \\mathbf{x}W_V'}</M>.
          Training shapes those matrices so that useful questions find useful answers — say, a pronoun’s query matching a noun’s
          key.
        </P>
        <Quiz
          id="q-learned"
          question="Who decides what a token’s query ‘asks for’?"
          options={[
            { label: 'Gradient descent — the W_Q, W_K, W_V matrices are learned like any other weights.', correct: true },
            { label: 'Programmers write rules for each word.', why: 'Nobody writes these. They emerge from training to predict the next token.' },
            { label: 'The query is the word itself, unchanged.', why: 'It’s a learned projection of the word’s vector, so the same word can ask different things in different heads.' },
          ]}
          explain="The whole mechanism is differentiable — dot products, softmax, weighted sums — so backpropagation can tune it. That’s why it learns."
        />
      </Step>
    </Lesson>
  )
}
